<script setup lang="ts">
/**
 * What a gated route shows on a plan without its feature (MAR-980, decision D2 by Alex, 2026-09-29).
 *
 * Rendered by `pages/app.vue` IN PLACE of the page, inside the app shell and at the address the user
 * asked for — the route used to redirect to `/app` without a word. The rail still hides gated
 * entries; this is only reached by a direct URL or a link.
 *
 * It names the LOWEST plan that includes the feature, and that name comes from the server
 * (`profile.feature_upgrade_plans`, resolved off the live plan grid by `lowestPlanWithFeature`) —
 * never from a list here, because the grid is editable. With no plan to name it still says what
 * happened.
 *
 * THE WAY IN IS A SEAT (MAR-1048 AC20; MAR-1039 D1): the plan belongs to the seat an admin assigns,
 * so a member is told to ask their admin for one, and an admin is taken straight to the seat screen
 * (`/app/settings/organization/members`). Neither is sent to email sales any more. The role comes
 * from `/api/orgs/info`; until it is known, the body is held back rather than guessed.
 */
import { Lock } from 'lucide-vue-next'
import type { PlanFeature } from '#shared/organizations/planFeatures'
import { Button } from '@/components/ui/button'

const props = defineProps<{ feature: PlanFeature }>()

const { t } = useI18n()
const { profile } = useUserProfile()
const { isAdmin, fetchOrg } = useOrgManagement()

const roleKnown = ref(false)
onMounted(async () => {
  try { await fetchOrg() }
  catch { /* The title still says what happened; the body falls back to the member's wording. */ }
  finally { roleKnown.value = true }
})

/** The feature by the name the rail and its page already use, so the notice reads as the same place. */
const FEATURE_LABEL_KEY: Record<PlanFeature, string> = {
  app: 'nav.chat',
  chat: 'nav.chat',
  case_management: 'nav.clients',
  sheets: 'sheets.title',
  workflows: 'nav.workflows',
  workflow_builder: 'nav.workflows',
  contract_review: 'nav.contractReview',
  landing_research: 'nav.chat',
  voice_transcription: 'nav.chat',
}

const featureLabel = computed(() => t(FEATURE_LABEL_KEY[props.feature]))
const planLabel = computed(() => profile.value?.feature_upgrade_plans?.[props.feature] ?? null)

/**
 * `app` is the seat itself (MAR-1212): a member with no license (or an `unpaid` one) has no plan to
 * name, so the notice says what is missing and who can give it, not which plan has a feature.
 */
const noSeat = computed(() => props.feature === 'app')

const title = computed(() => noSeat.value ? t('planGate.noSeatTitle') : t('planGate.title', { feature: featureLabel.value }))

const body = computed(() => {
  if (noSeat.value) return isAdmin.value ? t('planGate.noSeatAdminBody') : t('planGate.noSeatMemberBody')
  if (isAdmin.value) return planLabel.value ? t('planGate.adminBody', { plan: planLabel.value }) : t('planGate.adminBodyNoPlan')
  return planLabel.value ? t('planGate.memberBody', { plan: planLabel.value }) : t('planGate.memberBodyNoPlan')
})
</script>

<template>
  <section
    data-testid="plan-feature-notice"
    :data-feature="feature"
    class="flex h-full flex-col items-center justify-center px-6 py-24 text-center"
  >
    <Lock class="h-8 w-8 text-muted-foreground/60" aria-hidden="true" />
    <h1 class="mt-3 text-sm font-medium text-foreground">
      {{ title }}
    </h1>
    <p v-if="roleKnown" class="mt-1 max-w-sm text-sm text-muted-foreground" data-testid="plan-feature-notice-body">
      {{ body }}
    </p>
    <Button v-if="isAdmin" as-child variant="accent" class="mt-5 min-h-11">
      <NuxtLink to="/app/settings/organization/members" data-testid="plan-feature-notice-assign">
        {{ t('planGate.assignSeats') }}
      </NuxtLink>
    </Button>
  </section>
</template>
